import styles from "./CheckoutOnlineForm.module.scss";
import { useState } from "react";
import {
  Message,
  Button,
  Empty,
  Text,
  useAuth,
  ButtonGroup,
} from "@eventsquare/uikit";
import {
  Form,
  FormCheckbox,
  FormFieldset,
  FormGroup,
  FormInput,
  FormLabel,
  FormSelect,
  FormTextArea,
} from "@eventsquare/uikit/forms";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ChannelCart } from "@type/channelCart";

import { Api } from "@lib/api";
import { removeCookie } from "@lib/cookies";
import { RiMailSendLine } from "@remixicon/react";
import { hasSeatmapTypes } from "@lib/helpers";
import { getLocalStorage, setLocalStorage } from "@lib/localStorage";

interface CheckoutOnlineFormProps {
  cart: ChannelCart;
  setShowSwitch: (show: boolean) => void;
  languages: { value: string; label: string }[];
}

interface FormOnlineValues {
  email: string;
  reference: string;
  message: string;
  language: "nl" | "fr" | "en";
  validity_period: string;
  lock_places: boolean;
  note: string;
}

interface Error {
  response: {
    body: {
      message: string;
      error: string;
      validation: {
        cart?: string[];
        email?: string[];
        language?: string[];
        validity_period?: string[];
      };
    };
  };
}

const getInitialValidtyPeriod = (): string => {
  const savedValidityPeriod = getLocalStorage("invitation_validity_period");
  if (savedValidityPeriod) {
    return savedValidityPeriod;
  } else {
    return "43800";
  }
};

export const CheckoutOnlineForm = (props: CheckoutOnlineFormProps) => {
  const { cart, setShowSwitch, languages } = props;
  const { accountId } = useAuth();
  const { t } = useTranslation(["components_checkout_online_form", "common"]);
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);

  const [step, setStep] = useState<"form" | "success">("form"); // Should be form by default
  const [errors, setErrors] = useState<string[] | null>(null);

  const initialValues: FormOnlineValues = {
    email: "",
    reference: "",
    message: "",
    language: "nl",
    validity_period: getInitialValidtyPeriod(),
    lock_places: false,
    note: "",
  };

  const [submittedValues, setSubmittedValues] = useState<
    FormOnlineValues | undefined
  >(undefined);

  const handleSubmit = async (values: FormOnlineValues) => {
    try {
      setLoading(true);
      setErrors(null);

      const data: {
        cart: string;
        email: string;
        reference: string;
        message: string;
        validity_period: string | null;
        language: string;
        lock_places: 1 | 0;
        note?: string;
      } = {
        cart: cart.cartid,
        email: values.email.trim(),
        reference: values.reference.trim(),
        message: values.message.trim(),
        validity_period:
          values.validity_period === "never" ? null : values.validity_period,
        language: values.language,
        lock_places: values.lock_places ? 1 : 0,
      };

      if (values.note.trim() !== "") {
        data.note = values.note.trim();
      }

      const response = await Api.post(
        `/accounts/${accountId}/pos/reservations`,
        data,
      );
      if (response) {
        setShowSwitch(false);
        setSubmittedValues(values);
        removeCookie();
        setStep("success");
        setLocalStorage("invitation_validity_period", values.validity_period);
      }
    } catch (error) {
      if (Api.isApiError(error)) {
        //console.log(error.response);
      }
      const err = (error as Error).response?.body;
      if (err.error === "validation_failed") {
        const validationErrors: string[] = [];
        for (const [key, value] of Object.entries(err.validation)) {
          value.forEach((v: string) => {
            validationErrors.push(`${key}_${v}`);
          });
        }
        setErrors(validationErrors);
      } else {
        setErrors([err?.error ?? "something_went_wrong"]);
      }
    } finally {
      setLoading(false);
    }
  };

  const finishCheckout = async () => {
    await removeCookie();
    navigate("/");
  };

  return (
    <div className={styles.checkoutOnlineForm}>
      {step === "form" && (
        <>
          <Text variant="h5">{t("form.title")}</Text>
          <Text variant="p">{t("form.lead")}</Text>
          <Form onSubmit={handleSubmit} initialValues={initialValues}>
            {errors && errors.length >= 1 && (
              <Message
                alert
                type="error"
                message={errors.map((error) => t(`errors.${error}`))}
              />
            )}
            <FormFieldset>
              <FormGroup>
                <FormLabel htmlFor="firstname">{t("form.email")}</FormLabel>
                <FormInput name="email" type="email" />
              </FormGroup>
              <FormGroup>
                <FormLabel htmlFor="reference">{t("form.reference")}</FormLabel>
                <FormInput name="reference" type="text" />
              </FormGroup>
              <FormGroup>
                <FormLabel htmlFor="message" helperText={t("common:optional")}>
                  {t("form.message")}
                </FormLabel>
                <FormTextArea name="message" rows={5} />
              </FormGroup>
              <FormGroup>
                <FormLabel htmlFor="lang">{t("form.lang")}</FormLabel>
                <FormSelect
                  name="language"
                  placeholder={t("form.lang_placeholder")}
                  options={languages.sort((a, b) =>
                    a.label < b.label ? -1 : 1,
                  )}
                />
              </FormGroup>
              <FormGroup>
                <FormLabel htmlFor="validity_period">
                  {t("form.validity")}
                </FormLabel>
                <FormSelect
                  name="validity_period"
                  placeholder={t("form.validity_placeholder")}
                  options={[
                    { value: "4320", label: t("form.validity_3_days") },
                    { value: "10080", label: t("form.validity_1_week") },
                    { value: "43800", label: t("form.validity_1_month") },
                    { value: "262800", label: t("form.validity_6_months") },
                    { value: "525600", label: t("form.validity_1_year") },
                    { value: "1051200", label: t("form.validity_2_years") },
                    // { value: "1576800", label: t("form.validity_3_years") },
                    // { value: "never", label: t("form.validity_never") },
                  ]}
                />
              </FormGroup>
              {hasSeatmapTypes(cart) && (
                <FormGroup>
                  <FormCheckbox
                    name="lock_places"
                    label={t("form.lock_places")}
                  />
                </FormGroup>
              )}
              <FormGroup>
                <FormLabel htmlFor="note" helperText={t("common:optional")}>
                  {t("form.note")}
                </FormLabel>
                <FormTextArea name="note" />
              </FormGroup>
            </FormFieldset>
            <div className={styles.submitButton}>
              <ButtonGroup>
                <Button block type="submit" loading={loading}>
                  {t("form.submit")}
                </Button>
              </ButtonGroup>
            </div>
          </Form>
        </>
      )}
      {step === "success" && (
        <>
          <Empty
            icon={<RiMailSendLine />}
            title={t("success.title")}
            // TODO: Wrap form around th steps and get values from there
            lead={t("success.lead", { email: submittedValues?.email })}
          >
            <Button onClick={finishCheckout}>
              {t("success.finish_checkout")}
            </Button>
          </Empty>
        </>
      )}
    </div>
  );
};
